import { getSltViewAs } from './sltViewAs';

// Lifetime cap: each step shows at most this many times ever (localStorage, persists across tabs/sessions).
// Per-tab cap: each step shows at most once per browser tab session (sessionStorage, cleared on tab close).
const MAX_AUTO_SHOWS = 3;
const LS_PREFIX  = 'guide_count_';

// SLT read-only exploration of a teacher's workspace: never auto-show guides.
const inSltReadOnlyView = () => {
    try {
        return Boolean(getSltViewAs()?.teacherUid);
    } catch (err) {
        return false;
    }
};
const SS_PREFIX  = 'guide_seen_';
const LOGIN_COUNT_PREFIX = 'guide_login_count_';
const LOGIN_ACTIVE_KEY = 'guide_login_active_user';
const SUPPRESSED_LOGIN_COUNT = MAX_AUTO_SHOWS + 1;

let _userId = null;
const _shown = new Set(); // in-memory dedup within a single page load

const userKey = (uid = _userId) => uid || 'anon';
const lsKey = (key) => `${LS_PREFIX}${userKey()}_${key}`;
const ssKey = (key) => `${SS_PREFIX}${userKey()}_${key}`;
const loginCountKey = (uid = _userId) => `${LOGIN_COUNT_PREFIX}${userKey(uid)}`;

const getCount    = (key) => parseInt(localStorage.getItem(lsKey(key)) || '0', 10);
const incCount    = (key) => localStorage.setItem(lsKey(key), String(getCount(key) + 1));
const seenThisTab = (key) => sessionStorage.getItem(ssKey(key)) === '1';
const markThisTab = (key) => sessionStorage.setItem(ssKey(key), '1');

// Updates the current user context without recording a login.
export const setGuideUser = (uid) => {
    const newId = uid || null;
    if (newId !== _userId) {
        _userId = newId;
        _shown.clear();
    }
};

export const getGuideLoginCount = (uid = _userId) =>
    parseInt(localStorage.getItem(loginCountKey(uid)) || '0', 10);

// Claims the current browser tab's active guide-login slot without incrementing a count.
// The backend then supplies the authoritative cross-device login count.
export const claimGuideLogin = (uid) => {
    setGuideUser(uid);
    if (!uid) return false;

    if (sessionStorage.getItem(LOGIN_ACTIVE_KEY) === uid) {
        return false;
    }

    sessionStorage.setItem(LOGIN_ACTIVE_KEY, uid);
    _shown.clear();
    return true;
};

export const applyGuideLoginCount = (uid, count) => {
    setGuideUser(uid);
    if (!uid) return 0;

    const normalizedCount = Number.isFinite(Number(count)) ? Math.max(0, Number(count)) : 0;
    localStorage.setItem(loginCountKey(uid), String(normalizedCount));
    sessionStorage.setItem(LOGIN_ACTIVE_KEY, uid);
    _shown.clear();
    return normalizedCount;
};

export const suppressAutoGuideForUser = (uid = _userId) => applyGuideLoginCount(uid, SUPPRESSED_LOGIN_COUNT);

export const isProgressiveGuideLoginEligible = (uid = _userId) => {
    if (inSltReadOnlyView()) return false;
    const loginCount = getGuideLoginCount(uid);
    return loginCount >= 1 && loginCount <= MAX_AUTO_SHOWS;
};

// Legacy local fallback: counts one active login per user until logout clears it.
// Prefer claimGuideLogin + applyGuideLoginCount with the backend's authoritative count.
export const beginGuideLogin = (uid) => {
    setGuideUser(uid);
    if (!uid) return 0;

    if (sessionStorage.getItem(LOGIN_ACTIVE_KEY) === uid) {
        return getGuideLoginCount(uid);
    }

    const nextCount = getGuideLoginCount(uid) + 1;
    localStorage.setItem(loginCountKey(uid), String(nextCount));
    sessionStorage.setItem(LOGIN_ACTIVE_KEY, uid);
    _shown.clear();
    return nextCount;
};

// Call on logout so a fresh sign-in in the same tab counts as the next login.
export const endGuideLogin = () => {
    sessionStorage.removeItem(LOGIN_ACTIVE_KEY);
    setGuideUser(null);
};

// Returns true if step should NOT show: already shown this page load, this tab session, or lifetime cap hit.
export const hasShownThisSession = (key) =>
    inSltReadOnlyView() || _shown.has(key) || seenThisTab(key) || getCount(key) >= MAX_AUTO_SHOWS;

// Call when a guide step is about to be shown — marks all three layers.
export const markShownThisSession = (key) => {
    _shown.add(key);
    markThisTab(key);
    incCount(key);
};

// Resets in-memory Set (kept for compatibility — sessionStorage clears automatically on tab close).
export const clearGuideSession = () => _shown.clear();

// Wipe all persistent counts for the current user (e.g. for testing/account reset).
export const resetGuideCountsForUser = () => {
    const lsPrefix = `${LS_PREFIX}${userKey()}_`;
    const ssPrefix = `${SS_PREFIX}${userKey()}_`;
    Object.keys(localStorage).filter(k => k.startsWith(lsPrefix)).forEach(k => localStorage.removeItem(k));
    Object.keys(sessionStorage).filter(k => k.startsWith(ssPrefix)).forEach(k => sessionStorage.removeItem(k));
    localStorage.removeItem(loginCountKey());
    sessionStorage.removeItem(LOGIN_ACTIVE_KEY);
    _shown.clear();
};
